import React, { useState, useEffect } from 'react'; 
import { auth } from '../../firebase'; 
import { useNavigate } from 'react-router-dom';
import useLimits from '../../hooks/useLimits';
import { toast } from 'react-toastify';

const ContentPaste = ({ isOpen, onClose, onSubmit, onStartGlobalLoading, onStopGlobalLoading }) => {
  const [urlInput, setUrlInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const navigate = useNavigate();
  const { uploads } = useLimits();


  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setUrlInput('');
    setApiError(null);
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (uploads !== undefined && uploads <= 0) {
      setApiError('You have reached your daily upload limit.');
      return;
    }

    const url = urlInput.trim();
    if (!url) return;
    
    let finalId = null;

    try {
      if (!auth.currentUser) {
        setApiError('Authentication required. Please sign in.');
        return;
      }

      // Close modal first, then start the card overlay after a tick
      onClose();
      setTimeout(() => onStartGlobalLoading?.('Processing URL...'), 30);

      const idToken = await auth.currentUser.getIdToken(true);
      setIsLoading(true);
      setApiError(null);
        
      const PROCESS_URL_API = process.env.REACT_APP_PROCESS_URL_API;
      const endpoint = `${PROCESS_URL_API}?url=${encodeURIComponent(url)}&uid=${idToken}`;
      const response = await fetch(endpoint, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}` 
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 500) {
          toast.error('Access to origin blocked please paste content of webpage in chat');
        }
        throw new Error(`API error (${response.status}): ${errorText}`);
      }
      
      const data = await response.json();
      const contentId = data.document_id;

      const result = await onSubmit({ 
        type: 'url', 
        content: url, 
        data,
        contentId,
        source_type: "Web URL",
        source_url: url
      });

      finalId = contentId || result?.contentId || null;
    } catch (error) {
      setApiError(error.message);
      const result = await onSubmit({ 
        type: 'url', 
        content: url,
        source_type: "Web URL",
        source_url: url 
      }); // fallback
      finalId = result?.contentId || null;
    } finally {
      setIsLoading(false);
    }
    
    onStopGlobalLoading?.({ success: !!finalId, message: finalId ? 'Opening chat...' : 'Processing failed' });
    if (finalId) {
      setTimeout(() => {
        navigate(`/c/${finalId}`);
      }, 50);
    }
  };

  if (!isOpen) return null;

  const noUploadsLeft = uploads !== undefined && uploads <= 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black bg-opacity-70" 
        onClick={onClose}
      ></div>

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <form onSubmit={handleSubmit} className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Add Content</h2>

          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Enter a Webpage, PDF Document, or Research Paper URL
            </label>
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://arxiv.org/pdf/2408.xxxx.pdf"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
              disabled={noUploadsLeft}
            />
            {noUploadsLeft && (
              <div className="text-red-600 text-sm" role="alert" aria-live="polite">
                You have reached your daily upload limit.
              </div>
            )}

          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={isLoading || !urlInput.trim() || noUploadsLeft}
              className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors disabled:opacity-70 disabled:hover:bg-blue-600"
            >
              {isLoading ? 'Processing...' : 'Add'}
            </button>
          </div>

          {isLoading && (
            <div className="text-blue-600 mt-2">Processing content...</div>
          )}
          {apiError && !isLoading && (
            <div className="text-red-600 mt-2">{apiError}</div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ContentPaste;
